import isEmpty from 'lodash.isempty'

import {
  landingBreadcrumbFetch,
  landingDetailsFetch,
  landingNavigationSeoFetch,
  merchantDetailsFetch,
} from '@http/endpoints'
import {
  PRODUCT_PROVIDERS,
  PRODUCT_SOURCE,
  PRODUCT_SOURCE_TYPE,
} from '@tracking'

import translations from './landing.translations'

export const getDefaultState = () => ({
  id: null,
  breadcrumb: [],
  bannerInfo: {},
  hero: {},
  displayHeroAndRates: true,
  tagline: '',
  company: '',
  address: {},
  slug: '',
  type: null,
  ebType: null,
  title: '',
  subtitle: '',
  tags: [],
  seo: {
    title: '',
    description: '',
  },
  urls: {},
  links: {},
  link: {},
  listViewStructure: {},
  isVanishingDeals: false,
  landingSeoNavigation: {},
  electronicWaste: {},
  buyingGuide: {},
})

export default {
  namespaced: true,
  state: getDefaultState,
  mutations: {
    saveLanding(state, payload) {
      Object.assign(state, payload)
    },
    setBreadcrumb(state, payload) {
      state.breadcrumb = payload
    },
    setLandingSeoNavigation(state, payload) {
      state.landingSeoNavigation = payload
    },
    reset(state) {
      Object.assign(state, {
        ...getDefaultState(),
      })
    },
  },

  getters: {
    landingId: (state) => {
      return state.id
    },

    title: (state) => {
      return state.title
    },

    subtitle: (state) => {
      return state.subtitle
    },

    seoTitle: (state) => {
      return state.seo.title
    },

    seoText: (state) => {
      return state.seo.text
    },

    type: (state) => {
      return state.type
    },

    breadcrumb: (state) => {
      return state.breadcrumb
    },

    landingSeoNavigation: (state) => {
      return state.landingSeoNavigation
    },

    tagline: (state) => {
      return state.tagline
    },

    hero: (state) => {
      return state.hero || {}
    },

    heroTracking: (_, getters) => {
      const {
        id: listingId,
        product_id: productId,
        category,
        name,
        model,
        brand,
        color,
        gradeId,
        price,
        default_warranty: warrantyDuration,
        merchant_id: merchantId,
        recommendation_id: recommendationId,
      } = getters.hero

      return {
        id: productId,
        name,
        model,
        brand,
        color,
        category,
        variant: gradeId,
        warrantyDuration,
        merchantId,
        price,
        listingId,
        list: PRODUCT_SOURCE_TYPE.LP_HERO,
        source: recommendationId
          ? PRODUCT_SOURCE.RECOMMENDATIONS
          : PRODUCT_SOURCE.SEARCH,
        provider: recommendationId
          ? PRODUCT_PROVIDERS.EARLYBIRD
          : PRODUCT_PROVIDERS.ALGOLIA,
      }
    },

    slug: (state) => {
      return state.slug
    },

    urls: (state) => {
      return state.urls
    },

    links: (state) => {
      return state.links
    },

    bannerInfo: (state) => {
      return state.bannerInfo
    },

    shouldDisplayHeroAndRates: (state) => {
      return state.displayHeroAndRates && !isEmpty(state.hero)
    },

    electronicWaste: (state) => {
      return state.electronicWaste
    },

    buyingGuide: (state) => {
      return state.buyingGuide
    },

    tags: (state) => {
      return state.tags || []
    },

    isListView: (state) => {
      return !isEmpty(state.listViewStructure)
    },

    listViewStructure: (state) => {
      return state.listViewStructure
    },

    attributesToRetrieve: (state) => {
      if (isEmpty(state.listViewStructure)) {
        return []
      }

      const { title = [], table = [] } = state.listViewStructure

      return [
        title.map(({ attribute }) => attribute),
        table.map(({ attributes }) =>
          attributes.map(({ attribute }) => attribute),
        ),
      ].flat(Infinity)
    },

    isVanishingDeals: (state) => {
      return state.isVanishingDeals
    },
  },

  actions: {
    reset({ commit }) {
      commit('reset')
    },
    // NEW_URL_FORMAT: We need to keep slug for retro compatibility
    // NEW_URL_TASK, TODO: https://backmarket.atlassian.net/browse/URL-78
    async fetchProductLanding({ dispatch, commit }, { slug, id }) {
      const { payload } = await dispatch(
        'http/request',
        {
          request: landingDetailsFetch,
          queryParams: { slug, id },
        },
        { root: true },
      )

      commit('saveLanding', payload)
    },

    // NEW_URL_FORMAT: We need to keep slug for retro compatibility
    // NEW_URL_TASK, TODO: https://backmarket.atlassian.net/browse/URL-78
    async fetchLandingBreadcrumb({ dispatch, commit }, { slug, id }) {
      const { payload } = await dispatch(
        'http/request',
        {
          request: landingBreadcrumbFetch,
          queryParams: { slug, id },
        },
        { root: true },
      )

      const breadcrumb = [
        { slug: '/', title: this.$t(translations.home) },
        ...payload,
      ]

      commit('setBreadcrumb', breadcrumb)
    },
    clearLandingSeoNavigation({ commit }) {
      commit('setLandingSeoNavigation', {})
    },

    // NEW_URL_FORMAT: We need to keep slug for retro compatibility
    // NEW_URL_TASK, TODO: https://backmarket.atlassian.net/browse/URL-78
    async fetchLandingSeoNavigation({ dispatch, commit }, { slug, id }) {
      const { payload } = await dispatch(
        'http/request',
        {
          request: landingNavigationSeoFetch,
          queryParams: { slug, id },
        },
        { root: true },
      )

      commit('setLandingSeoNavigation', payload)
    },

    async fetchMerchantLanding({ dispatch, commit }, id) {
      const { payload } = await dispatch(
        'http/request',
        {
          request: merchantDetailsFetch,
          pathParams: { id },
        },
        { root: true },
      )

      commit('saveLanding', payload)
    },
  },
}
